import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from './cn';

export interface FieldProps {
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  /** Error message — shown in danger color, takes precedence over hint. */
  error?: ReactNode;
  /** Helper text shown below the control. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Label + control + hint/error wrapper (replaces antd Form.Item chrome). */
export function Field({ label, htmlFor, required, error, hint, children, className }: FieldProps) {
  /*
   * The label has to POINT AT its control.
   *
   * `htmlFor` was optional and no caller passed one, so every Field in the app rendered a visible
   * label that named nothing: a screen reader announced the control with no name at all and clicking
   * the label text did not focus it. So mint an id (exactly as Form.Item does) and clone it onto the
   * control. A plain-string label is also mirrored onto `aria-label`, because the button-based
   * controls (Select, DatePicker) take their name from their own contents rather than from a
   * <label> — the same text either way, so the visible and the announced name still match.
   */
  const autoId = useId();
  const id = htmlFor ?? autoId;
  const el = isValidElement(children) ? (children as ReactElement<Record<string, any>>) : null;
  const extra: Record<string, any> = {};
  if (el && el.props.id == null) extra.id = id;
  if (el && typeof label === 'string' && el.props['aria-label'] == null) extra['aria-label'] = label;
  const control = el && Object.keys(extra).length > 0 ? cloneElement(el, extra) : children;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label != null && (
        <label htmlFor={id} className="type-body-medium text-content">
          {label}
          {required && <span className="text-danger-500"> *</span>}
        </label>
      )}
      {control}
      {error != null ? (
        <span className="text-xs text-danger-500">{error}</span>
      ) : hint != null ? (
        <span className="text-xs text-muted">{hint}</span>
      ) : null}
    </div>
  );
}
